/**
 * @fileoverview Request My Page target reassignment mutation hook.
 * @description An Executive asks for a target to be handed to a colleague (POST
 * /sales/targets/{targetId}/reassignment-requests). Sends no If-Match: the target changes only if
 * the reviewer approves the request.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { SALES_QUERY_KEY } from '@/features/sales/constants/prospects-query-keys.constants'
import { requestMyPageReassignment } from '@/features/sales/services/my-page-targets.service'

import type { MyPageReassignmentRequestInput } from '@/features/sales/types/my-page.types'

/**
 * @description Sends the request, then refreshes every sales query so the table, KPIs and
 * details show the target's new state.
 * @param {() => void} [onSuccess] - Called after a success, even when the dialog has unmounted
 * since: it is an option of the mutation, not of a `mutate` call.
 * @returns {ReturnType<typeof useMutation>} The mutation (mutate with the target id, the proposed assignee and the reason).
 */
export function useRequestMyPageReassignment(onSuccess?: () => void) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      targetId,
      input,
    }: {
      targetId: string
      input: MyPageReassignmentRequestInput
    }) => requestMyPageReassignment(targetId, input),
    /**
     * @description Refreshes every sales query, then reports the success.
     */
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SALES_QUERY_KEY })
      onSuccess?.()
    },
  })
}
